import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CameraOutlined,
  CheckOutlined,
  CloseOutlined,
  CrownOutlined,
  DeleteOutlined,
  EditOutlined,
  LogoutOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal, Popconfirm, Spin, Tag, Tooltip, message } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../../stores/Store";
import {
  useAddGroupMembers,
  useChangeGroupMemberRole,
  useDisbandGroup,
  useGroupDetail,
  useLeaveGroup,
  useRemoveGroupMember,
  useRenameGroup,
  useUpdateGroupAvatar,
} from "../../../../hooks/useChatGroup";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { GroupMemberPicker } from "./GroupMemberPicker";
import { getProfileName, useChatUserProfiles } from "./useChatUserProfiles";

const MAX_AVATAR_SIZE = 5 * 1024 * 1024;

interface GroupInfoModalProps {
  open: boolean;
  groupId: string;
  groupName?: string;
  onClose: () => void;
  onLeft: (groupId: string) => void;
}

export const GroupInfoModal = memo(({ open, groupId, groupName, onClose, onLeft }: GroupInfoModalProps) => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  const { data: detail, isLoading, isFetching } = useGroupDetail(groupId, open);

  const rename = useRenameGroup();
  const updateAvatar = useUpdateGroupAvatar();
  const addMembers = useAddGroupMembers();
  const removeMember = useRemoveGroupMember();
  const changeRole = useChangeGroupMemberRole();
  const leaveGroup = useLeaveGroup();
  const disbandGroup = useDisbandGroup();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [newMemberIds, setNewMemberIds] = useState<string[]>([]);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = detail?.myRole === "ADMIN";
  const members = useMemo(() => detail?.members ?? [], [detail?.members]);
  const memberUserIds = useMemo(() => members.map((member) => member.userId), [members]);
  const profileByUserId = useChatUserProfiles(memberUserIds);

  useEffect(() => {
    if (!open) {
      setEditingName(false);
      setAdding(false);
      setNewMemberIds([]);
    }
  }, [open]);

  const startRename = useCallback(() => {
    setNameDraft(detail?.name ?? "");
    setEditingName(true);
  }, [detail?.name]);

  const submitRename = useCallback(() => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      message.error("Tên nhóm không được để trống");
      return;
    }
    if (trimmed === detail?.name) {
      setEditingName(false);
      return;
    }
    rename.mutate(
      { groupId, name: trimmed },
      {
        onSuccess: () => setEditingName(false),
        onError: () => message.error("Đổi tên thất bại"),
      },
    );
  }, [detail?.name, groupId, nameDraft, rename]);

  const pickAvatar = () => avatarInputRef.current?.click();

  const onAvatarSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_AVATAR_SIZE) {
      message.error("Ảnh tối đa 5MB");
      return;
    }
    updateAvatar.mutate({ groupId, file }, { onError: () => message.error("Cập nhật ảnh thất bại") });
  };

  const submitAddMembers = useCallback(() => {
    if (newMemberIds.length === 0) {
      setAdding(false);
      return;
    }
    addMembers.mutate(
      { groupId, userIds: newMemberIds },
      {
        onSuccess: () => {
          setNewMemberIds([]);
          setAdding(false);
        },
        onError: () => message.error("Thêm thành viên thất bại"),
      },
    );
  }, [addMembers, groupId, newMemberIds]);

  const onLeave = useCallback(() => {
    leaveGroup.mutate(groupId, {
      onSuccess: () => {
        onLeft(groupId);
        onClose();
      },
      onError: () => message.error("Rời nhóm thất bại"),
    });
  }, [groupId, leaveGroup, onClose, onLeft]);

  const onDisband = useCallback(() => {
    disbandGroup.mutate(groupId, {
      onSuccess: () => {
        onLeft(groupId);
        onClose();
      },
      onError: () => message.error("Giải tán nhóm thất bại"),
    });
  }, [disbandGroup, groupId, onClose, onLeft]);

  const loadingDetail = isLoading || (!detail && isFetching);

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      closable={false}
      zIndex={1600}
      width={560}
      centered
      className="[&_.ant-modal-content]:!overflow-hidden [&_.ant-modal-content]:!rounded-[18px] [&_.ant-modal-content]:!p-0"
    >
      {loadingDetail || !detail ? (
        <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 bg-[#f8fafc] text-[#667085]">
          <Spin size="large" />
          <span className="text-sm">Đang tải thông tin nhóm...</span>
        </div>
      ) : (
        <div className="bg-white">
          <div className="relative border-b border-[#e7edf5] bg-[#f8fbff] px-5 pb-5 pt-6">
            <button
              type="button"
              title="Đóng"
              onClick={onClose}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border-none bg-white text-[#667085] shadow-sm transition hover:bg-[#eef6ff] hover:text-[#0068ff]"
            >
              <CloseOutlined />
            </button>

            <div className="flex items-center gap-4 pr-10">
              <div className="relative shrink-0">
                <CommonAvatar
                  size={82}
                  src={detail.avatarUrl ?? undefined}
                  name={detail.name || groupName}
                  className="bg-[#0a2240] text-2xl font-semibold text-white"
                />
                {isAdmin && (
                  <>
                    <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={onAvatarSelected} />
                    <button
                      type="button"
                      title="Đổi ảnh nhóm"
                      onClick={pickAvatar}
                      disabled={updateAvatar.isPending}
                      className="absolute -bottom-1 -right-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-[#0068ff] text-white shadow-sm transition hover:bg-[#0058dc] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {updateAvatar.isPending ? <Spin size="small" /> : <CameraOutlined style={{ fontSize: 13 }} />}
                    </button>
                  </>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#0068ff]">Thông tin nhóm</div>
                {editingName ? (
                  <div className="flex max-w-[360px] items-center gap-1">
                    <Input
                      size="middle"
                      autoFocus
                      maxLength={255}
                      value={nameDraft}
                      onChange={(event) => setNameDraft(event.target.value)}
                      onPressEnter={submitRename}
                    />
                    <Button type="primary" icon={<CheckOutlined />} loading={rename.isPending} onClick={submitRename} />
                    <Button icon={<CloseOutlined />} onClick={() => setEditingName(false)} />
                  </div>
                ) : (
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-xl font-semibold text-[#17212f]">{detail.name}</span>
                    {isAdmin && (
                      <Tooltip title="Đổi tên nhóm">
                        <button
                          type="button"
                          onClick={startRename}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-none bg-white text-[#8a97a8] hover:text-[#0068ff]"
                        >
                          <EditOutlined />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                )}
                <div className="mt-1 flex items-center gap-2 text-sm text-[#667085]">
                  <span>{members.length} thành viên</span>
                  <span className="h-1 w-1 rounded-full bg-[#cbd5e1]" />
                  <span>{isAdmin ? "Bạn là quản trị viên" : "Bạn là thành viên"}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-5">
            {isAdmin && (
              <section className="rounded-xl border border-[#e7edf5] bg-white p-3 shadow-sm">
                {adding ? (
                  <div className="flex flex-col gap-3">
                    <GroupMemberPicker
                      value={newMemberIds}
                      onChange={setNewMemberIds}
                      excludeUserIds={memberUserIds}
                      placeholder="Tìm nhân viên để thêm..."
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => {
                          setAdding(false);
                          setNewMemberIds([]);
                        }}
                      >
                        Hủy
                      </Button>
                      <Button type="primary" loading={addMembers.isPending} onClick={submitAddMembers} disabled={newMemberIds.length === 0}>
                        Thêm {newMemberIds.length > 0 ? `(${newMemberIds.length})` : ""}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button block icon={<UserAddOutlined />} onClick={() => setAdding(true)} className="!h-10 !rounded-lg">
                    Thêm thành viên
                  </Button>
                )}
              </section>
            )}

            <section className="overflow-hidden rounded-xl border border-[#e7edf5] bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-[#eef3f8] px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-[#17212f]">Thành viên nhóm</div>
                  <div className="text-xs text-[#8a97a8]">Avatar và vai trò từng thành viên</div>
                </div>
                {isFetching && (
                  <div className="flex items-center gap-2 text-xs text-[#8a97a8]">
                    <Spin size="small" />
                    <span>Đang đồng bộ</span>
                  </div>
                )}
              </div>

              {isFetching && members.length === 0 ? (
                <div className="flex min-h-[180px] items-center justify-center text-sm text-[#667085]">
                  <div className="flex flex-col items-center gap-2">
                    <Spin />
                    <span>Đang tải danh sách thành viên...</span>
                  </div>
                </div>
              ) : members.length === 0 ? (
                <div className="flex min-h-[180px] items-center justify-center text-sm text-[#667085]">Chưa có thành viên.</div>
              ) : (
                <div className="h-[300px] overflow-y-auto bg-[#fbfdff] px-3 py-2">
                  <div className="space-y-2">
                    {members.map((member) => {
                      const profile = profileByUserId.get(member.userId);
                      const memberName = getProfileName(profile, member.fullName ?? member.userId);
                      const isMe = member.userId === myUserId;

                      return (
                        <div
                          key={member.userId}
                          className="flex min-h-[60px] items-center gap-3 rounded-lg bg-white px-3 py-2 shadow-[0_1px_0_rgba(15,23,42,0.04)] transition hover:bg-[#f8fafc]"
                        >
                          <span title={memberName} className="shrink-0">
                            <CommonAvatar size={42} src={profile?.avatarId} name={memberName} className="bg-[#0068ff] font-semibold text-white" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="truncate text-sm font-semibold text-[#17212f]">{memberName}</span>
                              {isMe && (
                                <span className="shrink-0 rounded-full bg-[#eef6ff] px-2 py-0.5 text-[11px] font-medium text-[#0068ff]">Bạn</span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              {member.role === "ADMIN" ? (
                                <Tag color="gold" icon={<CrownOutlined />} className="!m-0 !text-[11px] !leading-5">
                                  Quản trị
                                </Tag>
                              ) : (
                                <span className="text-xs text-[#8a97a8]">Thành viên</span>
                              )}
                              {profile?.employeeCode && <span className="truncate text-xs text-[#8a97a8]">{profile.employeeCode}</span>}
                            </div>
                          </div>

                          {isAdmin && !isMe && (
                            <div className="flex shrink-0 items-center gap-1">
                              <Tooltip title={member.role === "ADMIN" ? "Gỡ quyền quản trị" : "Phong quản trị"}>
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<CrownOutlined />}
                                  className={member.role === "ADMIN" ? "!text-amber-500" : "!text-[#8a97a8]"}
                                  loading={changeRole.isPending}
                                  onClick={() =>
                                    changeRole.mutate({ groupId, targetUserId: member.userId, role: member.role === "ADMIN" ? "MEMBER" : "ADMIN" })
                                  }
                                />
                              </Tooltip>
                              <Popconfirm
                                title="Xóa thành viên này khỏi nhóm?"
                                okText="Xóa"
                                cancelText="Hủy"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => removeMember.mutate({ groupId, targetUserId: member.userId })}
                              >
                                <Tooltip title="Xóa khỏi nhóm">
                                  <Button size="small" type="text" danger icon={<UserDeleteOutlined />} />
                                </Tooltip>
                              </Popconfirm>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            <section className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Popconfirm
                title="Rời khỏi nhóm này?"
                description="Bạn sẽ không nhận được tin nhắn của nhóm nữa."
                okText="Rời nhóm"
                cancelText="Hủy"
                okButtonProps={{ danger: true }}
                onConfirm={onLeave}
              >
                <Button danger block icon={<LogoutOutlined />} loading={leaveGroup.isPending} className="!h-10 !rounded-lg">
                  Rời nhóm
                </Button>
              </Popconfirm>

              {isAdmin && (
                <Popconfirm
                  title="Giải tán nhóm này?"
                  description="Toàn bộ tin nhắn sẽ bị xóa vĩnh viễn cho tất cả thành viên."
                  okText="Giải tán"
                  cancelText="Hủy"
                  okButtonProps={{ danger: true }}
                  onConfirm={onDisband}
                >
                  <Button danger block type="primary" icon={<DeleteOutlined />} loading={disbandGroup.isPending} className="!h-10 !rounded-lg">
                    Giải tán nhóm
                  </Button>
                </Popconfirm>
              )}
            </section>
          </div>
        </div>
      )}
    </Modal>
  );
});
